import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { AboutSection } from "@/components/sections/section-about";
import { BannerSection } from "@/components/sections/section-banner";
import {
  TransportSection,
  TransportSectionDefaults,
} from "@/components/sections/section-transport";
import { Button } from "@/components/ui/button";
import {
  BlogContentBlock,
  BlogPost,
  getPostBySlug,
  getRelatedPosts,
} from "@/data/blog-posts";
import { ArrowRight, Clock, Copy, Tag, User } from "lucide-react";

const BASE_URL = import.meta.env.VITE_APP_URL;

// ─── Share helpers ────────────────────────────────────────────────────────────
function shareLinkedIn(url: string) {
  window.open(
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    "_blank"
  );
}
function shareWhatsApp(title: string, url: string) {
  window.open(
    `https://wa.me/?text=${encodeURIComponent(title + " — " + url)}`,
    "_blank"
  );
}
function shareTwitter(title: string, url: string) {
  window.open(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    "_blank"
  );
}
function copyLink(url: string) {
  navigator.clipboard.writeText(url).then(() => {
    toast.success("Link copiado!");
  });
}

// ─── Share button row ─────────────────────────────────────────────────────────
function ShareButtons({ title, url }: { title: string; url: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-n-text-neutral mr-1">Compartilhar:</span>

      {/* LinkedIn */}
      <button
        onClick={() => shareLinkedIn(url)}
        aria-label="Compartilhar no LinkedIn"
        className="w-8 h-8 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
        </svg>
      </button>

      {/* WhatsApp */}
      <button
        onClick={() => shareWhatsApp(title, url)}
        aria-label="Compartilhar no WhatsApp"
        className="w-8 h-8 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.109.549 4.09 1.512 5.817L.055 23.27a.5.5 0 0 0 .618.633l5.598-1.453A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.896 0-3.664-.518-5.177-1.42l-.37-.219-3.827.994.995-3.739-.24-.386A9.945 9.945 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
        </svg>
      </button>

      {/* X / Twitter */}
      <button
        onClick={() => shareTwitter(title, url)}
        aria-label="Compartilhar no X"
        className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center hover:opacity-90 transition-opacity"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.73-8.835L1.254 2.25H8.08l4.259 5.632 5.905-5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </button>

      {/* Copy link */}
      <button
        onClick={() => copyLink(url)}
        aria-label="Copiar link"
        className="w-8 h-8 rounded-full bg-n-green-h-light text-n-text-green flex items-center justify-center hover:bg-n-green-s-light transition-colors"
      >
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Content block renderer ───────────────────────────────────────────────────
function ContentBlock({ block }: { block: BlogContentBlock }) {
  switch (block.type) {
    case "paragraph":
      return (
        <p className="text-n-text-neutral leading-7 mb-5 text-base">
          {block.text}
        </p>
      );
    case "heading":
      if (block.level === 2) {
        return (
          <h2 className="text-n-text-green text-2xl font-semibold mt-10 mb-4">
            {block.text}
          </h2>
        );
      }
      return (
        <h3 className="text-n-text-green text-xl font-semibold mt-7 mb-3">
          {block.text}
        </h3>
      );
    case "quote":
      return (
        <blockquote className="border-l-4 border-n-text-green bg-n-green-h-light rounded-r-xl pl-5 pr-4 py-4 my-6">
          <p className="text-n-text-green italic leading-relaxed">
            &ldquo;{block.text}&rdquo;
          </p>
        </blockquote>
      );
    case "list":
      return (
        <ul className="list-disc list-outside ml-5 mb-5 space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="text-n-text-neutral leading-6">
              {item}
            </li>
          ))}
        </ul>
      );
    case "image":
      return (
        <figure className="my-8">
          <img
            src={block.src}
            alt={block.caption ?? ""}
            className="rounded-xl w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/mudanca.svg";
            }}
          />
          {block.caption && (
            <figcaption className="text-xs text-n-text-neutral mt-2 text-center">
              | {block.caption}
            </figcaption>
          )}
        </figure>
      );
    default:
      return null;
  }
}

// ─── Related post card (compact) ─────────────────────────────────────────────
function RelatedCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col bg-white rounded-xl border border-n-beige overflow-hidden shadow-sm hover:shadow-md transition-all hover:bg-n-beige"
    >
      <div className="aspect-video bg-gray-100 overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/mudanca.svg";
          }}
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-n-text-green bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {post.category}
          </span>
          <span className="text-xs text-n-text-neutral flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {post.readTime}
          </span>
        </div>
        <p className="text-sm font-medium text-n-text line-clamp-2 group-hover:text-n-text-green transition-colors">
          {post.title}
        </p>
        <div className="flex items-center gap-1 text-n-text-green text-xs font-medium mt-3 group-hover:gap-2 transition-all">
          Leia mais <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </Link>
  );
}

// ─── Sidebar compact related card ────────────────────────────────────────────
function SidebarRelatedCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex gap-3 p-3 rounded-lg hover:bg-n-green-h-light transition-colors"
    >
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/mudanca.svg";
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-n-text line-clamp-3 group-hover:text-n-text-green transition-colors leading-snug">
          {post.title}
        </p>
        <span className="text-xs text-n-text-neutral mt-1 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {post.readTime}
        </span>
      </div>
    </Link>
  );
}

// ─── Newsletter box ────────────────────────────────────────────────────────────
function NewsletterBox() {
  const [email, setEmail] = useState("");

  return (
    <div className="border-2 border-dashed border-n-green-light rounded-xl p-5">
      <h3 className="text-n-text-green font-semibold text-base mb-1">
        Fique por dentro de tudo sobre transporte de veículos!
      </h3>
      <p className="text-xs text-n-text-neutral mb-4">
        Receba na sua inbox a nossa newsletter.
      </p>
      <input
        type="email"
        placeholder="Seu Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-n-text-green"
      />
      <Button className="w-full">Sign Up</Button>
      <p className="text-[10px] text-n-text-neutral mt-2 text-center">
        Ao clicar você confirma que concorda com nossa{" "}
        <Link to="/termos" className="underline hover:text-n-text-green">
          Termos e Condições
        </Link>
      </p>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = slug ? getPostBySlug(slug) : undefined;
  const related = post ? getRelatedPosts(post) : [];

  useEffect(() => {
    if (slug && !post) {
      navigate("/blog", { replace: true });
    }
  }, [slug, post, navigate]);

  if (!post) return null;

  const canonicalUrl = `${BASE_URL}/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-n-bg-1">
      <Helmet>
        <title>{post.title} | Nacional GO Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="author" content={post.author.name} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${BASE_URL}/og/webclip.png`} />
        <meta property="og:site_name" content="Nacional GO" />
        <meta property="article:author" content={post.author.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={`${BASE_URL}/og/webclip.png`} />
      </Helmet>

      <section className="py-10 md:py-14 bg-white">
        <div className="container">
          {/* Breadcrumb */}
          <p className="text-sm text-n-text-neutral mb-6">
            <Link to="/blog" className="hover:text-n-text-green transition-colors">
              &gt; Blog
            </Link>{" "}
            &gt;{" "}
            <span className="text-n-text">{post.category}</span>
          </p>

          <div className="flex flex-col lg:grid lg:grid-cols-[1fr_340px] gap-12">
            {/* ── Main article ── */}
            <article>
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-semibold text-n-text-green mb-6 leading-tight">
                {post.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-n-text-neutral mb-5 pb-5 border-b border-n-beige">
                <span className="flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  {post.author.name}
                </span>
                <span>{post.date}</span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {post.readTime}
                </span>
              </div>

              {/* Share top */}
              <div className="mb-6">
                <ShareButtons title={post.title} url={canonicalUrl} />
              </div>

              {/* Hero image */}
              <div className="aspect-[16/8] bg-gray-100 rounded-xl overflow-hidden mb-8">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/mudanca.svg";
                  }}
                />
              </div>

              {/* Article content */}
              <div className="prose-like">
                {post.content.map((block, i) => (
                  <ContentBlock key={i} block={block} />
                ))}
              </div>

              {/* Share bottom */}
              <div className="mt-10 pt-6 border-t border-n-beige">
                <ShareButtons title={post.title} url={canonicalUrl} />
              </div>

              {/* Tags */}
              <div className="mt-4 flex flex-wrap gap-2">
                {[post.category, ...post.tags].map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-n-green-h-light text-n-text-green border border-n-beige"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Author card */}
              <div className="mt-8 flex items-center gap-4 p-5 bg-n-green-h-light rounded-xl border border-n-beige">
                <div className="w-12 h-12 rounded-full bg-n-green-s-light flex items-center justify-center shrink-0">
                  <User className="h-6 w-6 text-n-text-green" />
                </div>
                <div>
                  <p className="font-semibold text-n-text-green text-sm">
                    {post.author.name}
                  </p>
                  <p className="text-xs text-n-text-neutral">{post.author.role}</p>
                </div>
              </div>

              {/* "Veja também" — visible on mobile/tablet below article */}
              {related.length > 0 && (
                <div className="mt-12 lg:hidden">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-n-text-green">
                      Veja também
                    </h2>
                    <Link to="/blog">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-n-text-green hover:bg-n-green-h-light"
                      >
                        Ver todos
                      </Button>
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {related.map((rp) => (
                      <RelatedCard key={rp.id} post={rp} />
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* ── Sidebar (desktop only) ── */}
            <aside className="hidden lg:flex flex-col gap-6 self-start lg:sticky lg:top-24">
              <NewsletterBox />

              {related.length > 0 && (
                <div className="bg-white border border-n-beige rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-n-text-green text-base">
                      Veja também:
                    </h3>
                    <Link
                      to="/blog"
                      className="text-xs text-n-text-green hover:underline flex items-center gap-1"
                    >
                      Ver todos <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                  <div className="flex flex-col divide-y divide-n-beige">
                    {related.map((rp) => (
                      <SidebarRelatedCard key={rp.id} post={rp} />
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>

          {/* "Veja também" full grid — desktop, below both columns */}
          {related.length > 0 && (
            <div className="mt-16 hidden lg:block">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-n-text-green">
                  Veja também
                </h2>
                <Link to="/blog">
                  <Button
                    variant="outline"
                    className="text-n-text-green hover:bg-n-green-h-light"
                  >
                    Ver todos
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-6">
                {related.map((rp) => (
                  <RelatedCard key={rp.id} post={rp} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <AboutSection />
      <TransportSection {...TransportSectionDefaults} />
      <BannerSection title="Faça agora uma cotação e agende o transporte do seu veículo !" />
    </div>
  );
}
